import { Guid } from "client_legacy/ndaw/guid";
import {
	HubItemChildNodeFormModel,
	HubItemGroupNodeFormModel
} from "./common";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ActionListItem,
	ActionListSearch,
	ActionListSubMenuHeader,
	FormattedText,
	NoResults,
	Typography
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { getClientId } from "client/Misc/clientId";
import { generateTrackingId } from "client/Misc/utils";
import { contains, normalizeForSearch } from "client/Data/searching";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";

export type GroupSelectionMenuProps = {
	containingGroupId?: Guid;
	moveToGroupTitle: string;
	groupsHierarchy: readonly HubItemGroupNodeFormModel[];
	onBack(): void;
	onMoveToGroup(targetGroupId: Guid): void;
};

export const GroupSelectionMenu = (props: GroupSelectionMenuProps): ReactElement => {
	const {
		containingGroupId,
		groupsHierarchy,
		moveToGroupTitle,
		onBack,
		onMoveToGroup
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");

	const normalizedSearchTerm: string = useMemo(
		() => normalizeForSearch(searchTerm),
		[searchTerm]
	);

	const doesMatchSearchTerm = useCallback(
		(item: HubItemChildNodeFormModel): boolean => {
			if (normalizedSearchTerm === "") {
				return true;
			}

			const doesNameContainSearchTerm: boolean = contains(
				item.name,
				normalizedSearchTerm
			);

			if (doesNameContainSearchTerm) {
				return true;
			}

			if (item.kind === HubItemCategoryNodeKind.Group) {
				return item
					.groupData
					.children
					.some(doesMatchSearchTerm);
			}

			return false;
		},
		[normalizedSearchTerm]
	);

	const filteredGroups = useMemo(
		(): readonly HubItemGroupNodeFormModel[] => groupsHierarchy
			.filter(doesMatchSearchTerm),
		[doesMatchSearchTerm, groupsHierarchy]
	);

	return (
		<>
			<ActionListSubMenuHeader
				text={moveToGroupTitle}
				onGoBack={onBack}
			/>
			<ActionListSearch
				onChange={setSearchTerm}
				icon="filter"
				placeholder={(
					<FormattedText
						text={localize("_FilterByProperties")}
						thingsToReplace={[
							{
								what: "{{Properties}}",
								filler: (
									<Typography weight="bold" isItalic>
										{localize("_Group")}
									</Typography>
								)
							}
						]}
					/>
				)}
			/>
			{filteredGroups.length === 0
				? (
					<NoResults
						icon="magnifying-glass"
						title={localize("_NoSearchResults")}
						message={localize("_PleaseTrySearchingWithAnotherTerm")}
						trackingId="no-search-results"
					/>
				)
				: filteredGroups.map(
					x => (
						<GroupItem
							key={getClientId(x)}
							data={x}
							containingGroupId={containingGroupId}
							onClickGroup={onMoveToGroup}
							doesMatchSearchTerm={doesMatchSearchTerm}
						/>
					)
				)
			}
		</>
	);
};
GroupSelectionMenu.displayName = "GroupSelectionMenu";

type GroupItemProps = {
	containingGroupId?: Guid;
	data: HubItemGroupNodeFormModel;
	indentionLevel?: number;
	onClickGroup(clientId: Guid): void;
	doesMatchSearchTerm(item: HubItemChildNodeFormModel): boolean;
};

const GroupItem = (props: GroupItemProps): ReactElement | null => {
	const {
		containingGroupId,
		data,
		indentionLevel = 0,
		onClickGroup,
		doesMatchSearchTerm
	} = props;

	const clientId: Guid = getClientId(data);

	return (
		<>
			<ActionListItem
				key={clientId}
				onClick={() => onClickGroup(clientId)}
				text={data.name}
				trackingId={generateTrackingId("group", `|${clientId}`)}
				indentionLevel={indentionLevel}
				isDisabled={clientId === containingGroupId}
			/>
			{data.groupData
				.children
				.map(
					x => x.kind === HubItemCategoryNodeKind.Group && doesMatchSearchTerm(x)
						? (
							<GroupItem
								key={getClientId(x)}
								data={x}
								containingGroupId={containingGroupId}
								onClickGroup={onClickGroup}
								indentionLevel={indentionLevel + 1}
								doesMatchSearchTerm={doesMatchSearchTerm}
							/>
						)
						: null
				)
			}
		</>
	);
};
GroupItem.displayName = "GroupItem";